"use client";

import * as React from "react";
import { Clock, PlayCircle, ArrowLeft, GraduationCap } from "lucide-react";

import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { faNum, faToman } from "@/lib/format";
import { openCourse, pushView } from "@/stores/useAppStore";

type Course = {
  id: string;
  slug: string;
  title: string;
  description: string;
  coverImage?: string | null;
  launchUrl?: string | null;
  level: string;
  duration: string;
  lessonsCount: number;
  priceToman: number;
  instructor: string;
  active: boolean;
  sortOrder: number;
};

function levelMeta(level: string): { label: string; cls: string } {
  const l = level.trim();
  if (l.includes("متوسط")) {
    return {
      label: "متوسط",
      cls: "border-amber-500/30 bg-amber-500/15 text-amber-600 dark:text-amber-400",
    };
  }
  if (l.includes("پیشرفته") || l.includes("حرفه‌ای")) {
    return {
      label: "پیشرفته",
      cls: "border-rose-500/30 bg-rose-500/15 text-rose-600 dark:text-rose-400",
    };
  }
  return {
    label: l || "مقدماتی",
    cls: "border-primary/30 bg-primary/15 text-primary",
  };
}

export function CoursesPreview() {
  const [courses, setCourses] = React.useState<Course[] | null>(null);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    fetch("/api/courses", { cache: "no-store" })
      .then((r) => r.json())
      .then((data: { courses?: Course[] }) => {
        if (alive) setCourses((data.courses ?? []).slice(0, 3));
      })
      .catch(() => {
        if (alive) setError(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section
      id="courses"
      aria-labelledby="courses-heading"
      className="border-y border-border/40 bg-muted/20 py-20 md:py-24"
    >
      <div className="mx-auto w-full max-w-7xl px-4">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              دوره‌های محبوب
            </span>
            <h2
              id="courses-heading"
              className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl"
            >
              یادگیری هوش مصنوعی را از همین‌جا شروع کن
            </h2>
            <p className="mt-3 text-muted-foreground">
              دوره‌های تعاملی با استاندارد SCORM که پیشرفت یادگیری شما را به‌صورت
              خودکار ذخیره می‌کنند.
            </p>
          </div>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => pushView("academy")}
          >
            مشاهده همه دوره‌ها
            <ArrowLeft className="size-4" />
          </Button>
        </div>

        {courses === null ? (
          !error && (
            <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-0 overflow-hidden"
                >
                  <Skeleton className="h-40 w-full rounded-none" />
                  <div className="flex flex-col gap-3 p-5">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-9 w-full rounded-md" />
                  </div>
                </div>
              ))}
            </div>
          )
        ) : error ? (
          <p className="mt-12 text-center text-sm text-destructive">
            بارگذاری دوره‌ها با خطا مواجه شد. لطفاً دوباره تلاش کنید.
          </p>
        ) : courses.length === 0 ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">
            در حال حاضر دوره‌ای برای نمایش وجود ندارد.
          </p>
        ) : (
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {courses.map((course) => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function CourseCard({ course }: { course: Course }) {
  const lvl = levelMeta(course.level);
  const hasImage = !!course.coverImage && course.coverImage.trim() !== "";

  return (
    <article
      className={cn(
        "group flex flex-col overflow-hidden rounded-2xl border border-border bg-card",
        "hover:border-primary/50 hover:shadow-lg transition",
      )}
    >
      {/* Cover */}
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-gradient-to-br from-primary/15 via-primary/5 to-transparent">
        {hasImage ? (
          <img
            src={course.coverImage as string}
            alt={course.title}
            className="size-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex size-full items-center justify-center">
            <span className="flex size-14 items-center justify-center rounded-2xl bg-primary/15 text-primary ring-1 ring-primary/20">
              <GraduationCap className="size-7" />
            </span>
          </div>
        )}
        <span className="absolute right-3 top-3">
          <Badge
            variant="outline"
            className={cn("backdrop-blur-sm", lvl.cls)}
          >
            {lvl.label}
          </Badge>
        </span>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col gap-3 p-5">
        <h3 className="text-lg font-semibold leading-snug">{course.title}</h3>
        <p className="line-clamp-2 text-sm text-muted-foreground">
          {course.description}
        </p>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Clock className="size-3.5 text-primary" />
            {course.duration}
          </span>
          <span className="flex items-center gap-1.5">
            <PlayCircle className="size-3.5 text-primary" />
            {faNum(course.lessonsCount)} درس
          </span>
          <span className="flex items-center gap-1.5">
            <GraduationCap className="size-3.5 text-primary" />
            {course.instructor}
          </span>
        </div>

        <div className="mt-auto flex items-center justify-between gap-3 pt-2">
          <div className="flex flex-col leading-tight">
            <span className="text-[11px] text-muted-foreground">قیمت دوره</span>
            <span
              className={cn(
                "text-base font-bold",
                course.priceToman === 0
                  ? "text-primary"
                  : "text-foreground",
              )}
            >
              {course.priceToman === 0 ? "رایگان" : faToman(course.priceToman)}
            </span>
          </div>
          <Button
            className="gap-2"
            onClick={() => openCourse(course.slug)}
          >
            مشاهده دوره
            <ArrowLeft className="size-4" />
          </Button>
        </div>
      </div>
    </article>
  );
}

export default CoursesPreview;
